import { useActor } from '@xstate/react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ActorRef, DoneInvokeEvent } from 'xstate';
import classNames from 'classnames';
import { EVENTS as RECORDER_EVENTS, STATES as RECORDER_STATES, STATES as START_RECORDING, RecorderInterface, mediaDeviceType } from '../interfaces/recorderInterface';
import { IWidgetMachineProps, EVENTS as WIDGET_EVENTS, STATES as WIDGET_STATES, TAGS as WIDGET_TAGS, VIDEO_CORRUPTED_STATES, MODAL_ERROR_KEY, VIDEO_ERRORS } from '../interfaces/widgetInterface';
import { Explanation } from './Explanation';
import { PreviewList } from './PreviewList';
import { VideoCamera } from './VideoCamera';
import { isMobile } from '../utils/device.utils';
import { TAKE_TYPES } from '../interfaces/storageInterface';
import { VideoQuestion } from './VideoQuestion';
import { DEFAULT_COUNT_DOWN, ENOUGH_RECORDING_TIME } from '../utils/constants.utils';
import { StartRecordingButton } from './ui/StartRecordingButton';
import { StartNowButton } from './ui/StartNowButton';
import { StopRecordingButton } from './ui/StopRecordingButton';
import { ALL_JOB_QUESTIONS_TYPE, ANSWER_TYPES, ASSESSMENT_QUESTIONS_TYPE } from '../interfaces/jobInterface';
import { AssessmentController } from './AssessmentController';
import { IMultipleAnswer } from '../interfaces/videoInterface';
import { TimesUp } from './TimesUp';
import { emitTrackEvent, EVENT_TYPES, ITrackEvent } from '../services/event.service';

interface IProps extends IWidgetMachineProps {
  recorderRef: ActorRef<DoneInvokeEvent<any>, any>;
  isSliderModalOpen: boolean;
  isWidgetMinimized: boolean;
  onOpenSliderModal: () => void;
  onCloseSliderModal: () => void;
  onRecorderRetry: () => void;
  onReInitRecorder: () => void;
  myinterviewRef: React.RefObject<HTMLDivElement>;
}

export const InnerView = React.forwardRef<HTMLVideoElement, IProps>(({
  widgetMachine, sendToWidget, recorderRef, isSliderModalOpen, isWidgetMinimized, onOpenSliderModal, onCloseSliderModal, onRecorderRetry,
  onReInitRecorder, myinterviewRef,
}, videoRef) => {
  const [recorderMachine, sendToRecorder] = useActor(recorderRef);
  const [isQuestionVideoWatched, setIsQuestionVideoWatched] = useState(false);
  const innerRef = useRef<HTMLDivElement>(null);
  const {
    previewRef, questions, currentQuestion, currentTake, recordingType, isConnected, checksState, widgetConfig, failedRecordingMessage, timer,
  } = widgetMachine.context;
  const {
    recordingTime, countdown, durations, videoDevices, audioDevices, selectedVideoDevice, selectedAudioDevice, microphoneRef, mediaStream, recordWithoutVideo,
  } = recorderMachine.context as RecorderInterface;
  const currentQuestionObj:ALL_JOB_QUESTIONS_TYPE = questions[currentQuestion - 1];
  const isAssessment = !!currentQuestionObj?.answerType && currentQuestionObj.answerType !== ANSWER_TYPES.VIDEO;
  const isThinkingTime = isAssessment ? !!currentQuestionObj?.partDuration : !!currentQuestionObj?.thinkingTime;
  const isSetupState = widgetMachine.matches(WIDGET_STATES.SETUP);
  const isExplanationState = widgetMachine.matches({ [WIDGET_STATES.INTERVIEW]: WIDGET_STATES.INTERVIEW__EXPLANATION });
  const isAssessmentState = widgetMachine.matches({ [WIDGET_STATES.INTERVIEW]: WIDGET_STATES.INTERVIEW__ASSESSMENT });
  const isTimesUpState = widgetMachine.matches(WIDGET_STATES.TIMES_UP);
  const isCameraDisplayed = widgetMachine.hasTag(WIDGET_TAGS.SHOW_CAMERA);
  const isPreviewState = widgetMachine.matches(WIDGET_STATES.PREVIEW);
  const isRecorderInErrorState = recorderMachine.matches(RECORDER_STATES.ERROR);
  const isRecorderInIdleState = recorderMachine.matches(RECORDER_STATES.IDLE);
  const isRecording = recorderMachine.matches({ [RECORDER_STATES.RECORDING]: START_RECORDING.COLLECTING_BLOBS });
  const isCountDown = recorderMachine.matches({ [RECORDER_STATES.RECORDING]: START_RECORDING.COUNT_DOWN });
  const isRetakeAvailable = currentTake < currentQuestionObj?.numOfRetakes;
  const totalRetakes = currentQuestionObj?.numOfRetakes;
  const isQuestionMode = recordingType === TAKE_TYPES.QUESTION;
  const lastUnuploadedQuestion = widgetConfig.video?.videos?.reduce((acc, v, idx) => (!v.uploaded ? idx + 1 : acc), 0);
  const isLastQuestion = currentQuestion === (isQuestionMode ? lastUnuploadedQuestion : questions.length);
  const isUploadingState = widgetMachine.matches(WIDGET_STATES.UPLOADING);
  const isConfirmState = widgetMachine.matches(WIDGET_STATES.CONFIRM);
  const isVideoQuestionState = widgetMachine.matches({ [WIDGET_STATES.INTERVIEW]: WIDGET_STATES.INTERVIEW__VIDEO_QUESTION });
  const canStartRecording = isExplanationState || (widgetMachine.hasTag(WIDGET_TAGS.CAN_START_RECORDING) && (!isThinkingTime || !isVideoQuestionState));
  const videoCorruptedErrorMessage: VIDEO_ERRORS | null = failedRecordingMessage !== VIDEO_CORRUPTED_STATES.NO_ERROR ? failedRecordingMessage : null;
  const connectionErrorMessage: MODAL_ERROR_KEY & 'CONNECTION' | null = !isConnected && isSetupState ? 'CONNECTION' : null;
  const permissionErrorMessage: MODAL_ERROR_KEY & 'MEDIA_PERMISSION' | null = isRecorderInErrorState ? 'MEDIA_PERMISSION' : null;
  const microphoneErrorMessage: MODAL_ERROR_KEY & 'NO_SOUND' | null = checksState.microphone === 'ERROR' ? 'NO_SOUND' : null;
  const errorType: MODAL_ERROR_KEY | null = videoCorruptedErrorMessage || permissionErrorMessage || connectionErrorMessage || microphoneErrorMessage;
  const isFullScreen = !widgetMachine.hasTag(WIDGET_TAGS.DISPLAY_OUTER_VIEW);
  const isRecorderCanChangeSettings = (isRecorderInIdleState && isSetupState) || (!!errorType && isRecording);
  const isQuestionDisplayed = widgetMachine.hasTag(WIDGET_TAGS.DISPLAY_QUESTION);

  useEffect(() => {
    if (isVideoQuestionState) setIsQuestionVideoWatched(false);
  }, [isVideoQuestionState]);

  useEffect(() => {
    if (isUploadingState) {
      myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-height', '100vh');
    } else {
      myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-height', 'calc(40vh - 100px)');
    }
  }, [isUploadingState]);

  useEffect(() => {
    if (mediaStream && !recordWithoutVideo) {
      const settings = mediaStream.getVideoTracks()[0]?.getSettings();
      const { aspectRatio, width, height } = settings;
      const _width: number = isMobile ? Math.min(Number(width), Number(height)) : Number(width);
      const _height: number = isMobile ? Math.max(Number(width), Number(height)) : Number(height);
      sendToWidget({ type: WIDGET_EVENTS.UPDATE_VIDEO_DIMENSIONS, data: { width: _width, height: _height } });
      let _aspectRatio = aspectRatio;
      if (_width && _height) _aspectRatio ??= _width / _height;
      if (_aspectRatio) {
        myinterviewRef?.current?.style.setProperty('--myinterview-widget-video-aspect-ratio', _aspectRatio.toString());
      }
      const cameraTransformVal = settings.facingMode === 'environment' ? 'translate3d(0, 0, 0)' : 'translate3d(0, 0, 0) rotateY(180deg)';
      myinterviewRef?.current?.style.setProperty('--myinterview-widget-camera-transform', cameraTransformVal);
    }
  }, [mediaStream]);

  useLayoutEffect(() => {
    if (isMobile) innerRef?.current?.scrollIntoView();
  }, [isRecording]);

  const handleDeviceChange = (type?: mediaDeviceType, id?:string) => {
    if (!sendToRecorder) return;
    if (isMobile && !type) sendToRecorder(RECORDER_EVENTS.CHANGE_VIDEO_DEVICE);
    else if (type === 'audio') sendToRecorder({ type: RECORDER_EVENTS.CHANGE_AUDIO_DEVICE, data: id });
    else if (type === 'video') sendToRecorder({ type: RECORDER_EVENTS.CHANGE_VIDEO_DEVICE, data: id });
  };

  const onVideoEnds = (watchedCount: number) => {
    if (isThinkingTime) {
      sendToWidget(WIDGET_EVENTS.NEXT_STEP);
    } else {
      setIsQuestionVideoWatched(true);
    }
    emitTrackEvent({
      eventType: EVENT_TYPES.VIDEO_QUESTION_WATCHED,
      extraData: { currentQuestion, watchedCount, isThinkingTime, url: currentQuestionObj.videoQuestion },
    });
  };

  const onVideoStateChanged = (eventData: ITrackEvent) => {
    emitTrackEvent({ ...eventData, extraData: { ...eventData.extraData, currentQuestion, isThinkingTime, url: currentQuestionObj.videoQuestion } });
  };

  const onNextQuestion = (questionNumber: number) => {
    sendToWidget({ type: WIDGET_EVENTS.NEXT_QUESTION, data: questionNumber });
  };

  const onSubmitAssessment = (answer: IMultipleAnswer[] | string, isTimesUp: boolean) => {
    sendToWidget({ type: WIDGET_EVENTS.NEXT_QUESTION, data: { answer, isTimesUp } });
  };

  const onContinue = () => {
    sendToWidget(WIDGET_EVENTS.NEXT_STEP);
  };

  const onNextRetake = () => {
    sendToWidget(WIDGET_EVENTS.NEXT_RETAKE);
  };

  const onQuestionMode = () => {
    sendToWidget(WIDGET_EVENTS.QUESTION_MODE);
  };

  const onStopRecording = () => {
    sendToRecorder?.(RECORDER_EVENTS.STOP_RECORDING);
  };

  useEffect(() => {
    if (isWidgetMinimized && isRecording) onStopRecording();
  }, [isWidgetMinimized, isRecording]);

  const innerClassNames = classNames({
    'myinterview-widget-inner': true,
    'myinterview-widget-inner--full': isFullScreen && !isExplanationState,
    'myinterview-widget-inner--hidden': isUploadingState || isConfirmState,
    'myinterview-widget-inner--video-question-state': isVideoQuestionState,
  });

  const contentClasseNames = classNames({
    'myinterview-widget-inner__content': true,
    'myinterview-widget-inner__content--full-width': isVideoQuestionState || (isQuestionDisplayed && !isRecording),
    'myinterview-widget-inner__content--error': isRecorderInErrorState || errorType,
  });

  return (
    <>
      <div ref={innerRef} className={innerClassNames}>
        <div className={contentClasseNames}>

          {isExplanationState && (
          <Explanation
            isVideoQuestion={!!currentQuestionObj?.videoQuestion}
            isAssessment={isAssessment}
            timeLimit={isAssessment ? currentQuestionObj.partDuration : currentQuestionObj.thinkingTime}
            answerType={currentQuestionObj.answerType || ANSWER_TYPES.VIDEO}
            duration={currentQuestionObj.partDuration}
            takes={currentQuestionObj.numOfRetakes}
          />
          )}

          <VideoCamera
            recordWithoutVideo={recordWithoutVideo}
            myinterviewRef={myinterviewRef}
            ref={videoRef}
            microphoneRef={microphoneRef}
            isCameraDisplayed={isCameraDisplayed}
            isPracticeModeDisplayed={!isQuestionMode && isRecording}
            errorType={errorType}
            isPermissionStepsOpen={isSliderModalOpen}
            onDisplayPermissionSteps={onOpenSliderModal}
            onReInitRecorder={onReInitRecorder}
            onClosePermissionSteps={onCloseSliderModal}
            onRecorderRetry={isSetupState ? onRecorderRetry : onReInitRecorder}
            isRecorderCanChangeSettings={isRecorderCanChangeSettings}
            videoDevices={videoDevices}
            audioDevices={audioDevices}
            selectedVideoDevice={selectedVideoDevice}
            selectedAudioDevice={selectedAudioDevice}
            handleDeviceChange={handleDeviceChange}
            canStartRecording={canStartRecording}
            currentQuestion={currentQuestion}
            numberOfQuestions={questions.length}
            currentQuestionObj={currentQuestionObj}
            isRecording={isRecording}
            durations={durations}
            recordingTime={recordingTime}
            isCountDown={isCountDown}
            countdown={countdown}
            isQuestionDisplayed={isQuestionDisplayed}
            isAssessment={isAssessment}
          />

          {isAssessmentState && !!currentQuestionObj?.answerType && currentQuestionObj?.answerType !== ANSWER_TYPES.VIDEO
          && (
          <AssessmentController
            currentQuestionObj={currentQuestionObj as ASSESSMENT_QUESTIONS_TYPE}
            timer={timer}
            currentQuestion={currentQuestion}
            numberOfQuestions={questions.length}
            onSubmitAssessment={onSubmitAssessment}
          />
          )}

          {isTimesUpState && <TimesUp onContinue={onContinue} />}

          {(isVideoQuestionState || (isExplanationState && !!currentQuestionObj?.videoQuestion)) && (
          <VideoQuestion
            src={currentQuestionObj?.videoQuestion || ''}
            isThinkingTime={isThinkingTime}
            onVideoEnds={onVideoEnds}
            isWidgetMinimized={isWidgetMinimized}
            hidden={isExplanationState}
            onVideoStateChanged={onVideoStateChanged}
          />
          )}

          {previewRef && isPreviewState && (
          <PreviewList
            myinterviewRef={myinterviewRef}
            previewRef={previewRef}
            onNextQuestion={onNextQuestion}
            onNextRetake={onNextRetake}
            onQuestionMode={onQuestionMode}
            isRetakeAvailable={isRetakeAvailable}
            totalRetakes={totalRetakes}
            isQuestionMode={isQuestionMode}
            isLastQuestion={isLastQuestion}
            isConnected={isConnected}
            currentQuestion={currentQuestion}
            numberOfQuestions={questions.length}
          />
          )}

          {canStartRecording && (
          <StartRecordingButton
            onClick={() => sendToWidget(WIDGET_EVENTS.NEXT_STEP)}
            disabled={!!currentQuestionObj?.videoQuestion && !isQuestionVideoWatched && isVideoQuestionState}
            isExplanationState={isExplanationState}
            isVideoQuestionState={isVideoQuestionState}
            isVideoQuestion={!!currentQuestionObj?.videoQuestion}
            isAssessment={isAssessment}
          />
          )}

          {isCountDown && countdown > DEFAULT_COUNT_DOWN && (
            <StartNowButton onClick={() => sendToRecorder?.(RECORDER_EVENTS.FORCE_START)} />
          )}

          {(isRecording && !errorType) && (
          <StopRecordingButton
            onClick={onStopRecording}
            disabled={recordingTime < ENOUGH_RECORDING_TIME}
          />
          )}

        </div>
      </div>
      {canStartRecording && (
      <StartRecordingButton
        onClick={() => sendToWidget(WIDGET_EVENTS.NEXT_STEP)}
        disabled={!!currentQuestionObj?.videoQuestion && !isQuestionVideoWatched && isVideoQuestionState}
        isExplanationState={isExplanationState}
        isVideoQuestionState={isVideoQuestionState}
        isVideoQuestion={!!currentQuestionObj?.videoQuestion}
        isAssessment={isAssessment}
      />
      )}

      {isCountDown && countdown > DEFAULT_COUNT_DOWN && (
        <StartNowButton onClick={() => sendToRecorder?.(RECORDER_EVENTS.FORCE_START)} />
      )}

      {isRecording && (
      <StopRecordingButton
        onClick={onStopRecording}
        disabled={recordingTime < ENOUGH_RECORDING_TIME}
      />
      )}
    </>
  );
});
